import { useState, useEffect } from 'react';
import adminApi from '@/utils/adminApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Plus, Trash2, ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { uploadToCloudinary } from '@/utils/cloudinaryUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to normalize image URLs
const normalizeImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('/api/uploads')) {
    return `${BACKEND_URL}${url}`;
  }
  if (url.startsWith('/uploads')) {
    return `${BACKEND_URL}/api${url}`;
  }
  if (url.includes('/uploads/')) {
    const uploadsPath = url.substring(url.indexOf('/uploads'));
    return `${BACKEND_URL}/api${uploadsPath}`;
  }
  return url;
};

export default function AdminGallery() {
  const [galleryItems, setGalleryItems] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const [newItem, setNewItem] = useState({
    name: '',
    status: 'Sold',
    image: '',
  });

  useEffect(() => {
    fetchGalleryItems();
  }, []);

  const fetchGalleryItems = async () => {
    try {
      const response = await adminApi.get(`/gallery`);
      setGalleryItems(response.data);
    } catch (error) {
      toast.error('Failed to load gallery');
    }
  };

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;

    const file = files[0];
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File is too large (max 10MB)');
      return;
    }

    setUploadingImage(true);

    try {
      toast.info(`Uploading ${file.name}...`);
      
      // Upload to Cloudinary
      const result = await uploadToCloudinary(file, 'gallery');

      setNewItem({ ...newItem, image: result.url });
      toast.success('Image uploaded to cloud!');
    } catch {
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileUpload(Array.from(e.dataTransfer.files));
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileUpload(Array.from(e.target.files));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!newItem.image) {
      toast.error('Please upload an image first');
      return;
    }

    if (!newItem.name.trim()) {
      toast.error('Please enter a name');
      return;
    }

    try {
      await adminApi.post(`/gallery`, newItem);
      toast.success('Added to gallery!');
      fetchGalleryItems();
      setIsDialogOpen(false);
      setNewItem({ name: '', status: 'Sold', image: '' });
    } catch {
      // Silently fail
      toast.error('Failed to add to gallery');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      await adminApi.delete(`/gallery/${id}`);
      toast.success('Deleted from gallery!');
      fetchGalleryItems();
    } catch {
      // Silently fail
      toast.error('Failed to delete');
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await adminApi.put(`/gallery/${id}`, { status: newStatus });
      toast.success('Status updated!');
      fetchGalleryItems();
    } catch {
      // Silently fail
      toast.error('Failed to update status');
    }
  };

  return (
    <div className="bg-white p-6 rounded-2xl border border-stone-100">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-2">
          <ImageIcon className="w-6 h-6 text-[#E85D75]" />
          <h2 className="text-2xl font-serif font-medium text-stone-800">Gallery Management</h2>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="add-gallery-item-button"
              onClick={() => setNewItem({ name: '', status: 'Sold', image: '' })}
              className="bg-[#E85D75] text-white hover:bg-[#D64560] rounded-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add to Gallery
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Add New Gallery Image</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Image Upload */}
              <div>
                <label className="text-sm font-medium text-stone-600 mb-2 block">
                  Upload Image *
                </label>
                
                {!newItem.image ? (
                  <div
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                      dragActive
                        ? 'border-[#E85D75] bg-[#E85D75]/5'
                        : 'border-stone-300 hover:border-[#E85D75]/50'
                    }`}
                  >
                    <div className="space-y-3">
                      <div className="text-4xl">📤</div>
                      <div>
                        <p className="text-sm font-medium text-stone-700">
                          Drag & drop image here
                        </p>
                        <p className="text-xs text-stone-500 mt-1">or click to browse</p>
                      </div>
                      <input
                        type="file"
                        id="gallery-file-upload"
                        accept="image/*"
                        onChange={handleFileInputChange}
                        className="hidden"
                      />
                      <Button
                        type="button"
                        onClick={() => document.getElementById('gallery-file-upload').click()}
                        variant="outline"
                        disabled={uploadingImage}
                      >
                        {uploadingImage ? 'Uploading...' : 'Browse Files'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="relative">
                    <img
                      src={normalizeImageUrl(newItem.image)}
                      alt="Preview"
                      className="w-full h-48 object-cover rounded-xl"
                    />
                    <button
                      type="button"
                      onClick={() => setNewItem({ ...newItem, image: '' })}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              {/* Name */}
              <div>
                <label className="text-sm font-medium text-stone-600 mb-2 block">
                  Kitten Name *
                </label>
                <Input
                  value={newItem.name}
                  onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                  placeholder="e.g., Shrimp, Luna, etc."
                  required
                />
              </div>

              {/* Status */}
              <div>
                <label className="text-sm font-medium text-stone-600 mb-2 block">
                  Status *
                </label>
                <Select
                  value={newItem.status}
                  onValueChange={(value) => setNewItem({ ...newItem, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Available">Available</SelectItem>
                    <SelectItem value="Reserved">Reserved</SelectItem>
                    <SelectItem value="Sold">Sold</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button
                type="submit"
                data-testid="save-gallery-item-button"
                className="w-full bg-[#E85D75] text-white hover:bg-[#D64560] rounded-full"
                disabled={!newItem.image || !newItem.name}
              >
                Add to Gallery
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Gallery Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {galleryItems.map((item) => (
          <div
            key={item.id}
            data-testid={`gallery-item-${item.id}`}
            className="relative group rounded-xl overflow-hidden border border-stone-100"
          >
            <img
              src={normalizeImageUrl(item.image)}
              alt={item.name}
              className="w-full aspect-square object-cover"
            />
            
            {/* Status Badge */}
            {item.status === 'Sold' && (
              <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 rounded-full text-xs font-bold">
                SOLD
              </div>
            )}
            {item.status === 'Reserved' && (
              <div className="absolute top-2 left-2 bg-yellow-500 text-white px-2 py-1 rounded-full text-xs font-bold">
                RESERVED
              </div>
            )}
            {item.status === 'Available' && (
              <div className="absolute top-2 left-2 bg-green-500 text-white px-2 py-1 rounded-full text-xs font-bold">
                AVAILABLE
              </div>
            )}

            {/* Delete Button - Always visible */}
            <Button
              onClick={() => handleDelete(item.id)}
              variant="destructive"
              size="sm"
              className="absolute top-2 right-2"
              data-testid={`delete-gallery-${item.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>

            {/* Info bar at bottom */}
            <div className="absolute bottom-0 left-0 right-0 bg-black/70 p-3">
              <p className="text-white font-medium text-sm mb-2">{item.name}</p>
              <Select
                value={item.status}
                onValueChange={(value) => handleStatusChange(item.id, value)}
              >
                <SelectTrigger className="w-full bg-white text-stone-800 h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Available">Available</SelectItem>
                  <SelectItem value="Reserved">Reserved</SelectItem>
                  <SelectItem value="Sold">Sold</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        ))}
      </div>

      {galleryItems.length === 0 && (
        <div className="text-center py-12 text-stone-500">
          <ImageIcon className="w-16 h-16 mx-auto mb-4 text-stone-200" />
          <p>No gallery images yet. Click "Add to Gallery" to get started.</p>
        </div>
      )}
    </div>
  );
}
